<script lang="ts" setup>
import type { StrapiPageError } from '@autobid/strapi-integration/typescript/strapi/Page'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useBodyStore } from '@autobid/nuxt-pinia-store/store/useBodyStore'
import { useHeaderStore } from '@autobid/nuxt-pinia-store/store/useHeaderStore'
import { useStrapiLang } from '@autobid/strapi-integration/composable/useStrapiLang'
import {
  parseStrapiLayouts,
  parseStrapiNavigation,
} from '@autobid/strapi-integration/utils/content'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { TRANSPARENT_NAVBAR_THRESHOLD } from '@autobid/ui/constants/TRANSPARENT_NAVBAR_THRESHOLD'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'

const emit = defineEmits(['error'])

const { strapiLang: lang } = useStrapiLang()
const { isAuthed } = useAutobidAuth()
const { ONE_TRUST_ID } = useRuntimeConfig().public
const { $trpc } = useNuxtApp()

const { suspense, data } = useQuery(
  ['layoutQuery', lang],
  async () => {
    try {
      const data = await $trpc.cms.getLayout.query({ lang: lang.value })

      if (!data?.layouts?.length) {
        emit('error', {
          code: 404,
          message: 'Layout not found',
        } as StrapiPageError)

        return null
      }

      const navigation = parseStrapiNavigation(
        isAuthed.value,
        data.renderNavigation,
      )

      const layout = parseStrapiLayouts(data.layouts)

      return {
        navigation,
        layout,
      }
    }
    catch (error) {
      const { message, code } = getError(error)

      emit('error', {
        code,
        message,
      } as StrapiPageError)

      return null
    }
  },
  {
    refetchOnWindowFocus: false,
    onError: (err) => {
      emit('error', getError(err))
    },
  },
)
onServerPrefetch(suspense)
await suspense()

const { scrollY } = storeToRefs(useBodyStore())
const { isMobileMenuVisible } = storeToRefs(useHeaderStore())
useHead({
  script:
    process.server && ONE_TRUST_ID?.length
      ? [
          data.value?.layout?.cookieAutoblockEnabled
            ? {
                src: `https://cdn.cookielaw.org/consent/${ONE_TRUST_ID}/OtAutoBlock.js`,
                crossorigin: 'anonymous',
              }
            : {},
          {
            'src': 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
            'data-domain-script': ONE_TRUST_ID,
            'crossorigin': 'anonymous',
          },
          {
            innerHTML: 'function OptanonWrapper() {}',
            type: 'text/javascript',
          },
        ]
      : [],
})

provide('isNavbarTransparent', data.value?.layout?.isNavbarTransparent)
</script>

<template>
  <div
    v-if="data"
    class="layout flex min-h-screen flex-col"
    :class="{
      'has-transparent-nav': data.layout?.isNavbarTransparent,
    }"
    :style="{
      '--text-hover':
        data.layout?.isNavbarTransparent
        && scrollY < TRANSPARENT_NAVBAR_THRESHOLD
        && !isMobileMenuVisible
          ? 'var(--top-navigation-transparent-text-hover-color)'
          : 'var(--top-navigation-text-hover-color)',
    }"
  >
    <slot :layout-data="data" />

    <LayoutBackToTop v-if="data.layout?.showBackToTop" />
  </div>
</template>
